/* eslint-disable react-refresh/only-export-components */
import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    useEffect,
    useRef,
} from 'react';

export type HelpPagePriority = 'section' | 'page' | 'overlay';

const PRIORITY_WEIGHTS: Record<HelpPagePriority, number> = {
    section: 1,
    page: 2,
    overlay: 3,
};

export interface HelpPageContextValue {
    currentPageId: string | null;
    registerHelpPage: (contextId: string, priority?: HelpPagePriority) => () => void;
}

const defaultContextValue: HelpPageContextValue = {
    currentPageId: null,
    registerHelpPage: () => () => {},
};

export const HelpPageContext = createContext<HelpPageContextValue>(defaultContextValue);

interface RegisteredHelpPage {
    id: string;
    contextId: string;
    priority: HelpPagePriority;
    order: number;
}

export const HelpPageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [pageStack, setPageStack] = useState<RegisteredHelpPage[]>([]);
    const orderRef = useRef(0);

    const registerHelpPage = useCallback((contextId: string, priority: HelpPagePriority = 'section') => {
        const id = Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
        const order = ++orderRef.current;
        setPageStack((prev) => [...prev, { id, contextId, priority, order }]);

        return () => {
            setPageStack((prev) => prev.filter((item) => item.id !== id));
        };
    }, []);

    const currentPageId = useMemo(() => {
        if (pageStack.length === 0) return null;
        let topItem = pageStack[0];
        for (let i = 1; i < pageStack.length; i++) {
            const item = pageStack[i];
            const itemWeight = PRIORITY_WEIGHTS[item.priority] ?? 1;
            const topWeight = PRIORITY_WEIGHTS[topItem.priority] ?? 1;
            if (itemWeight > topWeight || (itemWeight === topWeight && item.order > topItem.order)) {
                topItem = item;
            }
        }
        return topItem.contextId;
    }, [pageStack]);

    const value = useMemo(
        () => ({
            currentPageId,
            registerHelpPage,
        }),
        [currentPageId, registerHelpPage],
    );

    return <HelpPageContext.Provider value={value}>{children}</HelpPageContext.Provider>;
};

export function useHelpPageContext(): HelpPageContextValue {
    return useContext(HelpPageContext);
}

export function useRegisterHelpPage(contextId?: string | null, priority?: HelpPagePriority) {
    const { registerHelpPage } = useHelpPageContext();

    useEffect(() => {
        if (!contextId) return;
        const unregister = registerHelpPage(contextId, priority);
        return unregister;
    }, [contextId, priority, registerHelpPage]);
}

